/* eslint-disable import/no-extraneous-dependencies */
import { Box, MenuItem, Select } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import AudioPlayer, { RHAP_UI } from 'react-h5-audio-player';
import 'react-h5-audio-player/lib/styles.css';

const SPEED_OPTIONS = [1, 1.25, 1.5, 1.75, 2];

// A prop 'children' é a vaga de garagem para o seu botão Responder!
export default function AudioPlayerWithSpeed({ src, tone = 'incoming', children }) {
  const playerRef = useRef(null);
  const [speed, setSpeed] = useState(1);
  const speedRef = useRef(1);
  const [localAudioSrc, setLocalAudioSrc] = useState('');

  const [showDuration, setShowDuration] = useState(true);

  const isOutgoing = tone === 'outgoing';

  const mainColor = isOutgoing ? '#ffffff' : '#54656f';
  const railColor = isOutgoing ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.15)';
  const shadowColor = isOutgoing ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.2)';

  useEffect(() => {
    let objectUrl = '';

    if (src) {
      fetch(src)
        .then((res) => {
          if (!res.ok) throw new Error('Falha ao buscar o áudio');
          return res.blob();
        })
        .then((blob) => {
          objectUrl = URL.createObjectURL(blob);
          setLocalAudioSrc(objectUrl);
        })
        .catch((err) => {
          console.warn('Fallback ativado: erro ao criar blob, usando URL original.', err);
          setLocalAudioSrc(src);
        });
    }

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  const getAudio = () => playerRef.current?.audio?.current || null;

  const applySpeed = (nextSpeed) => {
    setSpeed(nextSpeed);
    speedRef.current = nextSpeed;

    const audio = getAudio();
    if (audio) {
      audio.playbackRate = nextSpeed;
      audio.defaultPlaybackRate = nextSpeed;
    }
  };

  const syncRate = () => {
    const audio = getAudio();
    if (audio) {
      audio.playbackRate = speedRef.current;
      audio.defaultPlaybackRate = speedRef.current;
    }
  };

  const handlePlay = () => {
    const currentAudio = getAudio();
    document.querySelectorAll('audio').forEach((el) => {
      if (currentAudio && el !== currentAudio && !el.paused) {
        el.pause();
      }
    });

    setShowDuration(false);
    syncRate();
  };

  const handleSeeking = () => {
    setShowDuration(false);
    syncRate();
  };

  const handleEnded = () => {
    setShowDuration(true);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        minWidth: '240px',

        '& .rhap_container': {
          padding: 0,
          background: 'transparent',
          boxShadow: 'none',
          display: 'flex !important',
          flexDirection: 'row !important',
          alignItems: 'center !important',
          width: '100%',
        },

        '& .rhap_controls-section': {
          margin: '0 8px 0 0 !important',
          flex: 'none !important',
        },
        '& .rhap_main-controls': {
          flex: 'none !important',
        },
        '& .rhap_main-controls-button': {
          color: mainColor,
          width: '38px',
          height: '38px',
          border: 'none',
          margin: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          '& svg': {
            width: '32px',
            height: '32px',
          }
        },

        '& .rhap_progress-section': {
          margin: '0 !important',
          flex: '1 1 auto !important',
          display: 'flex !important',
          alignItems: 'center !important',
          gap: '8px',
          width: '100%',
        },
        '& .rhap_progress-container': {
          margin: 0,
          flex: '1 1 auto !important',
          width: '100% !important',
          minWidth: '100px !important',
        },
        '& .rhap_time': {
          color: mainColor,
          fontVariantNumeric: 'tabular-nums',
          fontSize: '12px',
          minWidth: '36px',
          fontWeight: 500,
          whiteSpace: 'nowrap',
          textAlign: 'right',
        },

        '& .rhap_progress-bar': {
          height: '4px',
          backgroundColor: railColor,
          borderRadius: '4px',
          width: '100%',
        },
        '& .rhap_progress-filled': {
          backgroundColor: mainColor,
          borderRadius: '4px',
        },
        '& .rhap_progress-indicator': {
          width: '12px',
          height: '12px',
          marginLeft: '-6px',
          top: '-4px',
          backgroundColor: mainColor,
          boxShadow: `0 0 4px ${shadowColor}`,
        },
        '& .rhap_download-progress': {
          backgroundColor: 'transparent',
        },
      }}
    >
      <AudioPlayer
        ref={playerRef}
        src={localAudioSrc || src}
        preload="auto"
        layout="horizontal-reverse"
        showJumpControls={false}
        customAdditionalControls={[]}
        customVolumeControls={[]}
        defaultDuration="00:00"
        customControlsSection={[
          RHAP_UI.MAIN_CONTROLS
        ]}
        customProgressBarSection={[
          RHAP_UI.PROGRESS_BAR,
          showDuration ? RHAP_UI.DURATION : RHAP_UI.CURRENT_TIME,
        ]}
        autoPlayAfterSrcChange={false}
        onPlay={handlePlay}
        onLoadedMetaData={syncRate}
        onSeeked={syncRate}
        onSeeking={handleSeeking}
        onEnded={handleEnded}
        progressUpdateInterval={20}
      />

      {/* LINHA INFERIOR DIVIDIDA: {children} na esquerda, Select na direita */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          mt: 0.5
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {children}
        </Box>

        <Select
          size="small"
          value={speed}
          onChange={(e) => applySpeed(Number(e.target.value))}
          sx={{
            flex: 'none',
            minWidth: '60px',
            height: '24px',
            fontSize: '0.70rem',
            color: mainColor,
            borderRadius: '16px',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: railColor,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: mainColor,
            },
            '& .MuiSvgIcon-root': {
              color: mainColor,
              fontSize: '1.1rem',
              right: '4px'
            },
            '& .MuiSelect-select': {
              paddingRight: '20px !important',
              paddingLeft: '8px',
            }
          }}
        >
          {SPEED_OPTIONS.map((option) => (
            <MenuItem key={option} value={option} sx={{ fontSize: '0.85rem' }}>
              {option}x
            </MenuItem>
          ))}
        </Select>
      </Box>
    </Box>
  );
}
